'use client';

import { useEffect, useState } from 'react';

import { useSiteSettingsContext } from '@/core/providers/site-settings-provider';
import { cn } from '@/lib/utils';
import type { ISubSections } from '@/shared/actions/navigation.service';
import type { IHeaderProps } from '@/shared/components/header/header';
import { getSearchPagePath } from '@/shared/components/header/shared/get-search-page-path';
import { useHeaderStore } from '@/shared/components/header/stores/header-store-provider';
import { Icon } from '@/shared/ui/icon/icon';
import { InputSearch } from '@/shared/ui/input/input-search';

import { MobileMainMenu } from './mobile-main-menu';
import { MobileMenuTopBar } from './mobile-menu-top-bar';
import { MobileServiceMenu } from './mobile-service-menu';
import { MobileSubSection } from './mobile-sub-section';

export interface IMobileHeader extends IHeaderProps {
    searchButtonLabel: string;
    resetButtonLabel: string;
    searchPlaceholder: string;
    mobileMainMenuButtonLabel: string;
    mobileCloseMenuButtonLabel: string;
    mobileSeeMoreButtonLabel: string;
}

export function MobileHeader({
    navigation,
    phone,
    searchButtonLabel,
    resetButtonLabel,
    searchPlaceholder,
    mobileMainMenuButtonLabel,
    mobileCloseMenuButtonLabel,
    mobileSeeMoreButtonLabel,
}: IMobileHeader) {
    const [menuBarOpen, setMenuBarOpen] = useState(false);
    const [accordionOpen, setAccordionOpen] = useState(false);
    const [subsectionSelected, setSubsectionSelected] = useState<ISubSections | null>(null);
    const { overlayNotice, toggleOverlayNotice } = useHeaderStore(state => state);

    const { locale } = useSiteSettingsContext();
    const searchPagePath = getSearchPagePath(locale?.toString());

    const handleMenuOpen = (open: boolean) => {
        if (open) {
            setMenuBarOpen(true);
        } else {
            setMenuBarOpen(false);
            setSubsectionSelected(null);
        }
    };

    useEffect(() => {
        if (menuBarOpen) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }

        return () => document.body.classList.remove('overflow-hidden');
    }, [menuBarOpen]);

    return (
        <div className='overflow-hidden print:hidden'>
            <div className='flex gap-6 xl:hidden'>
                <div className='flex size-6 h-7 items-center justify-center' onClick={() => handleMenuOpen(true)}>
                    <Icon name='IcBurger' />
                </div>
            </div>

            {menuBarOpen && (
                <div className='fade-in animate-in bg-surface-light absolute top-0 left-0 z-50 flex h-full min-h-dvh w-full flex-col justify-between overflow-y-scroll duration-300 xl:hidden'>
                    <div className={cn('flex w-full flex-col items-center', { 'gap-10': !subsectionSelected })}>
                        <MobileMenuTopBar
                            phone={phone}
                            subsectionSelected={subsectionSelected}
                            setSubsectionSelected={setSubsectionSelected}
                            handleMenuOpen={handleMenuOpen}
                            mobileCloseMenuButtonLabel={mobileCloseMenuButtonLabel}
                            mobileMainMenuButtonLabel={mobileMainMenuButtonLabel}
                        />
                        {!subsectionSelected && (
                            <div className='flex w-full flex-col items-center gap-20'>
                                <MobileMainMenu
                                    mainNavigation={navigation.mainNavigation}
                                    setSubsectionSelected={setSubsectionSelected}
                                    setAccordionOpen={setAccordionOpen}
                                />
                                <MobileServiceMenu
                                    serviceMenu={navigation.serviceMenu}
                                    setSubsectionSelected={setSubsectionSelected}
                                />
                            </div>
                        )}
                        {subsectionSelected && <MobileSubSection subsectionSelected={subsectionSelected} />}
                    </div>

                    <div
                        className={cn('flex w-full flex-col items-center', {
                            'pt-20': subsectionSelected || accordionOpen,
                        })}>
                        <div className='mb-5 flex w-full justify-center px-5'>
                            <InputSearch
                                placeholder={searchPlaceholder}
                                searchButtonLabel={searchButtonLabel}
                                resetButtonLabel={resetButtonLabel}
                                searchPage={searchPagePath}
                            />
                        </div>

                        {overlayNotice.btnLabel && (
                            <button onClick={toggleOverlayNotice} className='w-full'>
                                <div className='bg-surface-deep text-inverse-default flex w-full flex-col items-center justify-center py-6'>
                                    <span className='text-body-lg-500'>{overlayNotice.btnLabel}</span>
                                    <span className='text-link-md-500 underline'>{mobileSeeMoreButtonLabel}</span>
                                </div>
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
